import { MutableRefObject, RefObject, useLayoutEffect, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

type Dimensions = {
    x: number;
    y: number;
    width: number;
    height: number;
    left: number;
    top: number;
    right: number;
    bottom: number;
};

export default function useMeasure(
    ref: RefObject<HTMLElement | null> | MutableRefObject<HTMLElement | null>
) {
    const [bounds, setContentRect] = useState<Dimensions>({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
    });

    useLayoutEffect(() => {
        if (ref.current === null) {
            return () => {};
        }

        let animationFrameId: number | null = null;
        const measure: ResizeObserverCallback = ([entry]) => {
            animationFrameId = window.requestAnimationFrame(() => {
                if (!ref.current || !entry) {
                    return;
                }

                const el = entry.target as HTMLElement;

                const x = el.offsetLeft;
                const y = el.offsetTop;
                const width = el.clientWidth;
                const height = el.clientHeight;

                setContentRect({
                    x: x,
                    y: y,
                    width: width,
                    height: height,
                    left: x,
                    top: y,
                    right: x + width,
                    bottom: y + height,
                });
            });
        };

        const observer = new ResizeObserver(measure);
        observer.observe(ref.current);

        return () => {
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }

            observer.disconnect();
        };
    }, [ref.current]);

    return bounds;
}
